import React from 'react';
import { ArrowLeft, MessageSquare as MessageSquareIcon, UserPlus as UserPlusIcon, Building2 as Building2Icon, Calendar as CalendarIcon, Users as UsersIcon, Heart as HeartIcon, MapPin as MapPinIcon } from 'lucide-react';
import { people, companies, feedPosts } from '../../data/mockData';
interface PersonProfileProps {
  personId: string;
  onNavigate: (page: string, id?: string) => void;
}
export function PersonProfile({
  personId,
  onNavigate
}: PersonProfileProps) {
  const person = people.find(p => p.id === personId);
  if (!person) {
    return <div className="text-center py-20 text-slate-500" data-id="element-707">
        Person nicht gefunden.
      </div>;
  }
  const company = companies.find(c => c.id === person.companyId);
  const personPosts = feedPosts.filter(fp => fp.authorId === person.id).slice(0, 5);
  return <div className="space-y-6" data-id="element-708">
      <button onClick={() => onNavigate('network-people')} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors" data-id="element-709">
        <ArrowLeft size={16} data-id="element-710" />
        Zurück zum Verzeichnis
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-711">
        {/* Left Column: Profile Card */}
        <div className="lg:col-span-1 space-y-6" data-id="element-712">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-713">
            {/* Profile Header */}
            <div className="h-24 bg-gradient-to-br from-emerald-400 to-teal-500 relative" data-id="element-714">
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2" data-id="element-715">
                <div className="relative" data-id="element-716">
                  <div className="w-20 h-20 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center text-2xl font-bold text-slate-600" data-id="element-717">
                    {person.avatar}
                  </div>
                  {person.isOnline && <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full" data-id="element-718"></div>}
                </div>
              </div>
            </div>

            <div className="pt-14 pb-6 px-6 text-center" data-id="element-719">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-720">
                {person.firstName} {person.lastName}
              </h1>
              <p className="text-sm font-medium text-slate-600 mt-1" data-id="element-721">
                {person.role}
              </p>
              <button onClick={() => onNavigate('company-profile', person.companyId)} className="inline-flex items-center gap-1.5 mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-id="element-722">
                <Building2Icon size={14} data-id="element-723" />
                {person.companyName}
              </button>

              {/* Action Buttons */}
              <div className="flex gap-2 mt-6" data-id="element-724">
                <button onClick={() => onNavigate('messages')} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors shadow-sm" data-id="element-725">
                  <MessageSquareIcon size={16} data-id="element-726" />
                  Nachricht
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-727">
                  <UserPlusIcon size={16} data-id="element-728" />
                  Vernetzen
                </button>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-729">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-730">
              Über mich
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed" data-id="element-731">
              {person.bio}
            </p>
          </div>

          {/* Interests */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-732">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-733">
              Interessen
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-734">
              {person.interests.map((interest, i) => <span key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg border border-emerald-100" data-id="element-735">
                  {interest}
                </span>)}
            </div>
          </div>
        </div>

        {/* Right Column: Stats + Activity */}
        <div className="lg:col-span-2 space-y-6" data-id="element-736">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4" data-id="element-737">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 text-center" data-id="element-738">
              <div className="mx-auto w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center mb-3" data-id="element-739">
                <CalendarIcon size={20} data-id="element-740" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-741">
                {person.eventsAttended}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-742">Events besucht</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 text-center" data-id="element-743">
              <div className="mx-auto w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-3" data-id="element-744">
                <UsersIcon size={20} data-id="element-745" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-746">
                {person.connectionsCount}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-747">Kontakte</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 text-center" data-id="element-748">
              <div className="mx-auto w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3" data-id="element-749">
                <MapPinIcon size={20} data-id="element-750" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-751">
                {new Date(person.joinedAt).toLocaleDateString('de-DE', {
                month: 'short',
                year: 'numeric'
              })}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-752">Mitglied seit</p>
            </div>
          </div>

          {/* Company Info Card */}
          {company && <div onClick={() => onNavigate('company-profile', company.id)} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex items-center gap-5 hover:shadow-md transition-all cursor-pointer group" data-id="element-753">
              <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-3xl flex-shrink-0" data-id="element-754">
                {company.logo}
              </div>
              <div className="flex-1 min-w-0" data-id="element-755">
                <h3 className="font-bold text-slate-900 group-hover:text-emerald-600 transition-colors" data-id="element-756">
                  {company.name}
                </h3>
                <p className="text-sm text-slate-500 truncate" data-id="element-757">
                  {company.description}
                </p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400" data-id="element-758">
                  <span data-id="element-759">{company.industry}</span>
                  <span data-id="element-760">•</span>
                  <span data-id="element-761">{company.employeeCount} Mitarbeiter</span>
                </div>
              </div>
            </div>}

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-762">
            <div className="p-6 border-b border-slate-100" data-id="element-763">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-764">
                Letzte Aktivitäten
              </h3>
            </div>
            {personPosts.length > 0 ? <div className="divide-y divide-slate-100" data-id="element-765">
                {personPosts.map(post => <div key={post.id} className="p-5" data-id="element-766">
                    <p className="text-sm text-slate-800 leading-relaxed" data-id="element-767">
                      {post.content}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-400" data-id="element-768">
                      <span data-id="element-769">{post.createdAt}</span>
                      <span className="flex items-center gap-1" data-id="element-770">
                        <HeartIcon size={12} data-id="element-771" /> {post.likes}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-772">
                        <MessageSquareIcon size={12} data-id="element-773" /> {post.comments}
                      </span>
                    </div>
                  </div>)}
              </div> : <div className="p-12 text-center text-slate-400" data-id="element-774">
                <p className="text-sm" data-id="element-775">Noch keine Beiträge veröffentlicht.</p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}